import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";

const Index = () => {
  const scrollToHowItWorks = () => {
    document
      .getElementById("how-it-works")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />

      <main>
        {/* HERO */}
        <section className="pt-24 md:pt-32 pb-20 md:pb-28">
          <div className="container mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="order-2 lg:order-1">
                <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.1] mb-6">
                  A family tree where every name has a voice.
                </h1>
                <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 max-w-xl">
                  Capture someone's voice, presence, and stories while they're still here. Future generations get to interact with their tribute — not just read their name.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button variant="hero" size="xl" className="group" asChild>
                    <Link to="/tribute/syrus-demo">
                      View Demo
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                  <Button
                    variant="hero-outline"
                    size="xl"
                    onClick={scrollToHowItWorks}
                  >
                    How LegacyLens Works
                  </Button>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="rounded-2xl overflow-hidden border border-border shadow-card">
                  <img
                    src="/hero-family-tree-ar.jpg"
                    alt="A name in a family tree becoming an interactive ancestor through LegacyLens AR"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FRICTION */}
        <section className="py-16 md:py-24">
          <div className="mx-auto max-w-3xl px-6">
            <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight leading-tight mb-8">
              A family tree tells you who someone was. It doesn't tell you who they actually were.
            </h2>
            <div className="space-y-5 text-lg leading-relaxed">
              <p>
                Names. Dates. Sometimes a photograph. That's what most people inherit when someone in their family is gone.
              </p>
              <p>
                Their voice is missing. The way they told stories. Why they made the choices they made. The things only they knew.
              </p>
              <p>
                All of it disappears — not all at once, but slowly. Into abstraction.
              </p>
              <p>
                Photo albums freeze a moment. Written stories preserve language. Neither is an interactive tribute to the person who lived them.
              </p>
            </div>
          </div>
        </section>

        {/* FOUNDER */}
        <section className="py-16 md:py-24 border-t border-border/40">
          <div className="mx-auto max-w-3xl px-6">
            <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight leading-tight mb-8">
              I started LegacyLens because I didn't want to lose my chance.
            </h2>
            <div className="space-y-5 text-lg leading-relaxed">
              <p>
                I had been working on a different version of this for months. Then I went to visit my parents and realized something simpler: I might never see them again.
              </p>
              <p>
                So I captured them. The best I could. Their voice. Their 3D presence. The way they tell a story.
              </p>
              <p>
                That decision wasn't a product. It was personal.
              </p>
              <p>
                It became LegacyLens.
              </p>
            </div>
            <p className="mt-8 text-base">
              <Link
                to="/about"
                className="font-medium underline-offset-4 hover:underline text-foreground"
              >
                Read more about why I built this →
              </Link>
            </p>
          </div>
        </section>

        {/* ASPIRATION */}
        <section className="py-16 md:py-24 border-t border-border/40">
          <div className="mx-auto max-w-3xl px-6">
            <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight leading-tight mb-8">
              More than names on a tree. Don't just read about them.
            </h2>
            <div className="space-y-5 text-lg leading-relaxed">
              <p>
                Genealogy can tell you when your great-grandfather was born and where the family came from.
              </p>
              <p>
                LegacyLens lets you ask him about it.
              </p>
              <p>
                Tap a name in your family tree. Hear them in their own voice. See them at full scale. Ask them where the family came from, what they believed, what they remember.
              </p>
              <p>
                The names in your family tree become the people who lived them.
              </p>
            </div>
          </div>
        </section>

        {/* PRODUCT */}
        <section className="py-16 md:py-24 border-t border-border/40">
          <div className="mx-auto max-w-3xl px-6">
            <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight leading-tight mb-8">
              A system for preserving someone — while they're still here.
            </h2>
            <div className="space-y-5 text-lg leading-relaxed">
              <p>
                LegacyLens captures voice, presence, and story through a guided interview with the subject. Their consent is required. Their answers are theirs. The result is a tribute that families can return to over generations — accessed through a simple QR code, viewable in AR, and interactive through their own preserved voice.
              </p>
              <p>
                No special hardware. No app required. Captured once, accessible always.
              </p>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section
          id="how-it-works"
          className="py-16 md:py-24 border-t border-border/40"
        >
          <div className="mx-auto max-w-5xl px-6">
            <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mb-12 text-center">
              How It Works
            </h2>
            <div className="grid md:grid-cols-3 gap-8 md:gap-10">
              <div>
                <div className="font-display text-5xl text-muted-foreground/50 mb-4">
                  01
                </div>
                <h3 className="font-display text-2xl font-semibold tracking-tight mb-3">
                  Capture
                </h3>
                <p className="text-base leading-relaxed text-muted-foreground">
                  The subject answers a guided set of questions in their own voice — about their origins, their turning points, the people they loved, what they want their family to remember. Their voice is recorded. Their likeness is captured as a 3D model through photogrammetry, so they can later appear in augmented reality at full scale.
                </p>
              </div>
              <div>
                <div className="font-display text-5xl text-muted-foreground/50 mb-4">
                  02
                </div>
                <h3 className="font-display text-2xl font-semibold tracking-tight mb-3">
                  Preserve
                </h3>
                <p className="text-base leading-relaxed text-muted-foreground">
                  Voice, 3D presence, and story are preserved. Nothing is published without the subject's review. Conversations with the tribute aren't recorded. The{" "}
                  <Link
                    to="/digital-legacy-directive"
                    className="font-medium underline-offset-4 hover:underline text-foreground"
                  >
                    Digital Legacy Directive
                  </Link>{" "}
                  governs how everything is held — consent-first, revocable, built to last beyond us.
                </p>
              </div>
              <div>
                <div className="font-display text-5xl text-muted-foreground/50 mb-4">
                  03
                </div>
                <h3 className="font-display text-2xl font-semibold tracking-tight mb-3">
                  Connect
                </h3>
                <p className="text-base leading-relaxed text-muted-foreground">
                  A QR code in your family tree, on a printed chart, or at a memorial opens an AR experience and a conversation. Future generations don't just read about the people who came before them. They interact.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CATEGORY */}
        <section className="py-16 md:py-24 border-t border-border/40">
          <div className="mx-auto max-w-3xl px-6">
            <p className="text-lg leading-relaxed">
              LegacyLens isn't a memorial product, though memorials are part of what we make possible. We're genealogy infrastructure with the people in it. A way for the names in a family tree — and the lives behind them — to remain reachable across generations.
            </p>
          </div>
        </section>

        {/* B2B */}
        <section className="py-16 md:py-24 border-t border-border/40">
          <div className="mx-auto max-w-3xl px-6">
            <p className="text-lg leading-relaxed mb-6">
              For ancestry platforms, genealogy services, funeral homes, and cemeteries: LegacyLens partners with the institutions that already steward family memory.
            </p>
            <p className="text-base">
              <Link
                to="/partner"
                className="font-medium underline-offset-4 hover:underline text-foreground"
              >
                Partner with us →
              </Link>
            </p>
          </div>
        </section>

        {/* FOOTER CTA */}
        <section className="py-20 md:py-28 border-t border-border/40">
          <div className="mx-auto max-w-3xl px-6 text-center">
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight mb-6">
              There's still time.
            </h2>
            <p className="text-lg leading-relaxed text-muted-foreground mb-10 max-w-2xl mx-auto">
              The people in your life have stories you've never asked about — and stories you've never thought to record. LegacyLens captures them now — in a way that keeps them reachable for generations after.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="hero" size="xl" className="group" asChild>
                <Link to="/tribute/syrus-demo">
                  View Demo
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
              <Button variant="hero-outline" size="xl" asChild>
                <Link to="/get-started">Get Started</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
